import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "../../components/admin/AdminLayout";
import { Check, X } from "@phosphor-icons/react";

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  async function fetchSettings() {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rs_token")}` },
      });
      if (!res.ok) throw new Error("Failed");
      setSettings(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function save() {
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("rs_token")}`,
        },
        body: JSON.stringify(settings),
      });
      if (!res.ok) throw new Error("Failed to save");
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  const toggleBool = (key) => {
    const val = settings[key];
    setSettings({ ...settings, [key]: typeof val === "boolean" ? !val : !(val === "true" || val === true) });
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        {loading ? (
          <div className="flex items-center justify-center h-64"><div className="tag">Loading...</div></div>
        ) : (
          <>
            <div className="card-pop p-5 space-y-4">
              <h3 className="font-bold">Platform Controls</h3>
              <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: "var(--border)" }}>
                <div>
                  <div className="font-medium">Maintenance Mode</div>
                  <div className="text-xs text-[var(--ink-muted)]">Block all non-admin access</div>
                </div>
                <button
                  onClick={() => toggleBool("maintenance_mode")}
                  className={`w-12 h-6 rounded-full transition-colors relative ${settings.maintenance_mode ? "bg-orange-500" : "bg-gray-300"}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${settings.maintenance_mode ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>
              
              {/* Maintenance message */}
              <div className="py-2">
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Maintenance Message</label>
                <textarea
                  className="input-pop w-full mt-1.5 text-sm"
                  rows={2}
                  value={settings.maintenance_message || ""}
                  onChange={e => setSettings({ ...settings, maintenance_message: e.target.value })}
                  placeholder="Shown to users during maintenance..."
                />
              </div>
              
              {/* Maintenance ETA */}
              <div className="py-2">
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Expected Return Time (optional)</label>
                <input
                  className="input-pop w-full mt-1.5 text-sm"
                  value={settings.maintenance_eta || ""}
                  onChange={e => setSettings({ ...settings, maintenance_eta: e.target.value })}
                  placeholder="e.g. Back by 6 PM IST"
                />
              </div>
              
              {/* Support details */}
              <div className="grid sm:grid-cols-2 gap-3 py-2 border-t" style={{ borderColor: "var(--border)" }}>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Support Email</label>
                  <input
                    type="email"
                    className="input-pop w-full mt-1.5 text-sm"
                    value={settings.support_email || ""}
                    onChange={e => setSettings({ ...settings, support_email: e.target.value })}
                    placeholder="support@ratingally.com"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Support Phone</label>
                  <input
                    className="input-pop w-full mt-1.5 text-sm"
                    value={settings.support_phone || ""}
                    onChange={e => setSettings({ ...settings, support_phone: e.target.value })}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between py-2 border-b" style={{ borderColor: "var(--border)" }}>
                <div>
                  <div className="font-medium">Registration Enabled</div>
                  <div className="text-xs text-[var(--ink-muted)]">Allow new sign-ups</div>
                </div>
                <button
                  onClick={() => toggleBool("registration_enabled")}
                  className={`w-12 h-6 rounded-full transition-colors relative ${settings.registration_enabled ? "bg-orange-500" : "bg-gray-300"}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${settings.registration_enabled ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <div className="font-medium">Default Screenshot Required</div>
                  <div className="text-xs text-[var(--ink-muted)]">For new outlets</div>
                </div>
                <button
                  onClick={() => toggleBool("default_require_screenshot")}
                  className={`w-12 h-6 rounded-full transition-colors relative ${settings.default_require_screenshot ? "bg-orange-500" : "bg-gray-300"}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${settings.default_require_screenshot ? "translate-x-6" : "translate-x-0.5"}`} />
                </button>
              </div>
            </div>

            <div className="card-pop p-5 space-y-3">
              <h3 className="font-bold">Default Spin Wheel Segments</h3>
              <p className="text-sm text-[var(--ink-muted)]">Applied to newly created outlets</p>
              <pre className="text-xs bg-gray-50 p-3 rounded-lg overflow-auto max-h-48">
                {JSON.stringify(settings.default_segments || [], null, 2)}
              </pre>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-1.5">
                <Check size={14} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}